import { z } from 'zod';

// Курсы валют к USD (#149). Курс — строка «единиц валюты за 1 USD»; точная арифметика и
// пороги — в @upme/domain (fx-rate.ts). Клиенты ⚡ не пересчитывают: число приходит полем DTO.

export const FxRateSourceSchema = z.enum(['AUTO', 'MANUAL', 'IDENTITY']);
export const FxUnavailableReasonSchema = z.enum(['no_rate', 'stale_rate', 'amount_too_large']);
export const FxRateDecimalSchema = z
  .string()
  .regex(/^\d{1,16}(\.\d{1,8})?$/)
  .refine((s) => /[1-9]/.test(s), 'rate_must_be_positive');
export const CurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);

export const AdminFxRateDtoSchema = z.object({
  currency: CurrencyCodeSchema,
  cities: z.array(z.object({ id: z.uuid(), name: z.string(), isActive: z.boolean() })),
  auto: z
    .object({
      rate: FxRateDecimalSchema,
      asOf: z.iso.datetime(),
      fetchedAt: z.iso.datetime(),
      /** asOf + 72 ч — фронт не знает порога. */
      staleAt: z.iso.datetime(),
      provider: z.string(),
    })
    .nullable(),
  manual: z
    .object({
      rate: FxRateDecimalSchema,
      setAt: z.iso.datetime(),
      expiresAt: z.iso.datetime(),
      /** expiresAt > now */
      isActive: z.boolean(),
      setBy: z
        .object({ id: z.uuid(), displayName: z.string().nullable(), username: z.string().nullable() })
        .nullable(),
      note: z.string().nullable(),
    })
    .nullable(),
  effective: z.discriminatedUnion('available', [
    z.object({
      available: z.literal(true),
      rate: FxRateDecimalSchema,
      source: FxRateSourceSchema,
      asOf: z.iso.datetime(),
    }),
    z.object({ available: z.literal(false), reason: z.enum(['no_rate', 'stale_rate']) }),
  ]),
  /** false для USD. */
  editable: z.boolean(),
});
export type AdminFxRateDto = z.infer<typeof AdminFxRateDtoSchema>;

export const AdminFxRatesListDtoSchema = z.object({
  energyPerUsd: z.number().int().positive(),
  maxAgeHours: z.number().int().positive(),
  manualMaxTtlDays: z.number().int().positive(),
  items: z.array(AdminFxRateDtoSchema),
});
export type AdminFxRatesListDto = z.infer<typeof AdminFxRatesListDtoSchema>;

export const AdminFxManualRateInputSchema = z.object({
  rate: FxRateDecimalSchema,
  expiresAt: z.iso.datetime(),
  note: z.string().trim().max(200).optional(),
});
export type AdminFxManualRateInput = z.infer<typeof AdminFxManualRateInputSchema>;

export const AdminFxQuoteQuerySchema = z.object({
  currency: CurrencyCodeSchema,
  price: z.coerce.number().int().positive().max(2_147_483_647),
});
export type AdminFxQuoteQuery = z.infer<typeof AdminFxQuoteQuerySchema>;

export const EnergyQuoteDtoSchema = z.discriminatedUnion('available', [
  z.object({
    available: z.literal(true),
    price: z.number().int(),
    currency: CurrencyCodeSchema,
    energy: z.number().int().positive(),
    usdCents: z.number().int().nonnegative(),
    rate: FxRateDecimalSchema,
    rateAsOf: z.iso.datetime(),
    source: FxRateSourceSchema,
    energyPerUsd: z.number().int().positive(),
  }),
  z.object({
    available: z.literal(false),
    price: z.number().int(),
    currency: CurrencyCodeSchema,
    reason: FxUnavailableReasonSchema,
  }),
]);
export type EnergyQuoteDto = z.infer<typeof EnergyQuoteDtoSchema>;
